import { useCallback, useMemo } from "react";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";

import { Banner, Callout, CalloutRefresh, RatesForm } from "../components";
import {
  CityRate,
  CityRateDicounts,
  Rate,
  useConfig,
  UserRatesUpdateReq,
  useUser,
  useUserIncallRatesUpdate,
  useUserOutcallRatesUpdate,
} from "../services";
import { LoadingScreen } from "./loading";

type RatesScreenProps = {
  config: CityRate[];
  currencyCode?: string;
  discounts: CityRateDicounts;
  loading?: boolean;
  onSubmit: (req: UserRatesUpdateReq) => Promise<void>;
  rates: Rate[];
};

function RatesContent({
  config,
  currencyCode,
  discounts,
  loading,
  onSubmit,
  rates,
}: RatesScreenProps) {
  // Vars
  const hasDiscounts = useMemo(() => {
    return Object.values(discounts).reduce((acc, val) => acc + val, 0) > 0;
  }, [discounts]);

  const infoMessage = useMemo(() => {
    const messages = [];

    if (currencyCode) {
      messages.push(`All amounts are in ${currencyCode}.`);
    }

    if (hasDiscounts) {
      messages.push(
        `"Premium price" is the discounted price Premium clients get.`
      );
    }

    return messages;
  }, [currencyCode, hasDiscounts]);

  // Render
  return (
    <>
      {!!infoMessage.length && <Banner message={infoMessage.join("|")} />}
      <RatesForm
        config={config}
        discounts={discounts}
        loading={loading}
        onSubmit={onSubmit}
        rates={rates}
      />
    </>
  );
}

function IncallsScreen() {
  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refreshUser,
  } = useUser();
  const {
    data: config,
    isLoading: isConfigLoading,
    refetch: refreshConfig,
  } = useConfig();
  const [updateUserIncallRates, isUpdating] = useUserIncallRatesUpdate();

  // Methods
  const refresh = useCallback(() => {
    refreshUser();
    refreshConfig();
  }, [refreshUser, refreshConfig]);

  // Handlers
  const handleSubmit = useCallback(
    async (req: UserRatesUpdateReq) => {
      await updateUserIncallRates(req);

      alert("Incall rates updated successfully!");
    },
    [updateUserIncallRates]
  );

  // Render
  if (isUserLoading || isConfigLoading) {
    return <LoadingScreen />;
  }

  if (!user || !config) {
    return <CalloutRefresh onRefresh={refresh} />;
  }

  if (!config.incalls || !config.discounts_incall) {
    return (
      <Callout
        // icon="cash-multiple"
        title={`No incall in ${user.city?.name}`}
        message={`You cannot offer incalls in ${user.city?.name} yet.`}
      />
    );
  }

  return (
    <RatesContent
      config={config.incalls}
      currencyCode={user.city?.currency_code}
      discounts={config.discounts_incall}
      loading={isUpdating}
      onSubmit={handleSubmit}
      rates={user.incalls}
    />
  );
}

function OutcallsScreen() {
  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refreshUser,
  } = useUser();
  const {
    data: config,
    isLoading: isConfigLoading,
    refetch: refreshConfig,
  } = useConfig();
  const [updateUserOutcallRates, isUpdating] = useUserOutcallRatesUpdate();

  // Methods
  const refresh = useCallback(() => {
    refreshUser();
    refreshConfig();
  }, [refreshUser, refreshConfig]);

  // Handlers
  const handleSubmit = useCallback(
    async (req: UserRatesUpdateReq) => {
      await updateUserOutcallRates(req);

      alert("Outcall rates updated successfully!");
    },
    [updateUserOutcallRates]
  );

  // Render
  if (isUserLoading || isConfigLoading) {
    return <LoadingScreen />;
  }

  if (!user || !config) {
    return <CalloutRefresh onRefresh={refresh} />;
  }

  return (
    <RatesContent
      config={config.outcalls}
      currencyCode={user.city?.currency_code}
      discounts={config.discounts_outcall}
      loading={isUpdating}
      onSubmit={handleSubmit}
      rates={user.outcalls}
    />
  );
}

const Tab = createMaterialTopTabNavigator();

export function RatesScreen() {
  return (
    <Tab.Navigator>
      <Tab.Screen name="Outcalls" component={OutcallsScreen} />
      <Tab.Screen name="Incalls" component={IncallsScreen} />
    </Tab.Navigator>
  );
}
